/**
 * "Plays on" indicator (2026-09-29) - which computer this timeline plays on.
 *
 * A timeline plays on its HOME: the machine its device-bound steps drive (a
 * Helix preset plays where the Helix is plugged in), else the primary. See
 * `timeline/bridgeSync.ts::timelinePlacement`. Shown because the operator
 * could not see it before: their Helix timeline was quietly playing on the
 * Gaming-PC, which has no Helix, while the Helix sat on the Mac Studio.
 *
 * Same `.bridge-status` pill chassis as TimelineSyncPill/OpbridgeBadge, reused
 * deliberately so the editor header gains no new visual language (DEC-121).
 *
 * 2026-10-25: it also says whose CLOCK that computer is on (`timeline/homeClock.ts`), amber when the timeline is
 * probably not in time with Live: the kick/snare chase played on STREAMPC's own clock while Live led on the Mac, and
 * the pill said only "Plays on STREAMPC".
 */

import { Show, createMemo } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { timelineOfflineNote, timelineParts, timelinePlacement } from '../timeline/bridgeSync.ts';
import { bridges } from '../opbridge/store.ts';
import { clockVerdict, createClockStatus } from '../timeline/homeClock.ts';

interface TimelinePlaysOnPillProps {
  timeline: Timeline;
}

export function TimelinePlaysOnPill(props: TimelinePlaysOnPillProps) {
  const placement = createMemo(() => timelinePlacement(props.timeline));
  // Memos, so an edit that recomputes the placement but keeps the same computer does not restart the reading.
  const name = createMemo(() => placement().home?.name?.trim() || null);
  const homeId = createMemo(() => placement().home?.id ?? null);
  const status = createClockStatus(homeId);
  const clock = createMemo(() => {
    const s = status();
    const home = name();
    return s && home ? clockVerdict(s, home, bridges().length, (node) => bridges().find((b) => b.nodeId === node)?.name?.trim() || null) : null;
  });
  const where = () => (placement().basis === 'song'
    ? `Plays on ${name()}: its song is set to play there, so the timeline starts there and keeps the song in time. Lights and devices on other computers still play there.`
    : placement().basis === 'devices'
    ? `Plays on ${name()}: the devices on this timeline (a Helix, say) are there, so every step, plain notes included, is sent from that computer.`
    : `Plays on ${name()}, your main bridge: none of this timeline's steps is tied to a particular computer.`);
  // A computer holding a part is off (2026-10-28): the rest plays, and this says what does not, before Play is pressed.
  const offline = createMemo(() => timelineOfflineNote(props.timeline));
  const offNames = createMemo(() => timelineParts(props.timeline).offline.map((o) => o.cfg.name?.trim() || 'a computer'));
  const title = () => [where(), clock()?.detail, offline()].filter(Boolean).join('\n\n');
  const short = () => [clock()?.short, offNames().length > 0 ? `${offNames().join(' and ')} off` : null].filter(Boolean).join(', ');
  // A Genre timeline plays NOWHERE yet: OPBridge has no runtime for the tier
  // (plan 3.4). Saying "Plays on <machine>" for one was a false promise.
  const genre = () => props.timeline.tier === 'genre';

  return (
    <Show
      when={!genre()}
      fallback={
        <div
          class="bridge-status timeline-plays-on-pill"
          data-state="warn"
          data-basis="genre"
          title="Genre timelines can't play yet: OPBridge has no runtime for them. This one is kept, and will play once it can."
          aria-label="Won't play yet"
        >
          <span class="led" aria-hidden="true" />
          <span class="msg timeline-plays-on-pill-msg">Won't play yet</span>
        </div>
      }
    >
    <Show when={name()}>
      <div
        class="bridge-status timeline-plays-on-pill"
        data-state={clock()?.warn || offline() ? 'warn' : 'none'}
        data-basis={placement().basis}
        data-clock={clock()?.kind}
        data-offline={offline() ? 'true' : undefined}
        title={title()}
        aria-label={short() ? `Plays on ${name()}, ${short()}` : `Plays on ${name()}`}
      >
        <span class="led" aria-hidden="true" />
        <span class="msg timeline-plays-on-pill-msg">Plays on {name()}{short() ? `, ${short()}` : ''}</span>
      </div>
    </Show>
    </Show>
  );
}
